/**
 * Dialog store — imperative API for the app-wide confirm / prompt dialogs.
 *
 * Replaces the browser's `window.confirm` / `window.prompt`. A single
 * `<DialogHost />` rendered in `AppLayout` consumes this store. The
 * imperative API returns a Promise so existing `if (confirm(...))` call
 * sites become `if (await dialog.confirm(...))` — minimal diff.
 *
 *   const ok = await dialog.confirm({
 *     title: 'Discard quote?',
 *     message: 'Any unsaved changes will be lost.',
 *     confirmLabel: 'Discard',
 *     tone: 'destructive',
 *   });
 *
 *   const reason = await dialog.prompt({
 *     title: 'Reject claim',
 *     message: 'Reason (visible to the submitter)',
 *     required: true,
 *   });
 *
 * Only one dialog is active at a time. Opening a second resolves the first
 * with a cancel (false / null) before showing the second.
 */

import { create } from 'zustand';

export type DialogTone = 'default' | 'destructive';

interface BaseSpec {
  title: string;
  message: string;
  /** Defaults to "Confirm" for confirm dialogs and "OK" for prompts. */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** "destructive" paints the confirm button red. */
  tone?: DialogTone;
}

export interface ConfirmSpec extends BaseSpec {
  kind: 'confirm';
}

export interface PromptSpec extends BaseSpec {
  kind: 'prompt';
  placeholder?: string;
  /** If true, the confirm button stays disabled until the input is non-empty
   *  (post-trim). Default: false. */
  required?: boolean;
  /** Initial value for the input. */
  defaultValue?: string;
}

type ActiveDialog =
  | (ConfirmSpec & { resolve: (value: boolean) => void })
  | (PromptSpec & { resolve: (value: string | null) => void });

interface DialogState {
  active: ActiveDialog | null;
  /** Internal: open a dialog. Resolvers are wired by the public helpers. */
  open: (dialog: ActiveDialog) => void;
  /** Resolve the active dialog and close it. Called by the host on
   *  confirm/cancel and by the public helpers when another dialog opens
   *  before the current one resolves. */
  resolve: (value: boolean | string | null) => void;
}

export const useDialogStore = create<DialogState>((set, get) => ({
  active: null,
  open: (next) => {
    const prev = get().active;
    if (prev) {
      // Resolve the prior dialog with a cancel signal so its caller's
      // Promise settles instead of dangling. Match the resolver shape per
      // dialog kind.
      if (prev.kind === 'confirm') prev.resolve(false);
      else prev.resolve(null);
    }
    set({ active: next });
  },
  resolve: (value) => {
    const current = get().active;
    if (!current) return;
    if (current.kind === 'confirm') current.resolve(value as boolean);
    else current.resolve(value as string | null);
    set({ active: null });
  },
}));

/**
 * Imperative dialog API. Use anywhere — React component, hook, plain
 * service function. Returns a Promise that settles when the user picks
 * an action (or the dialog is superseded).
 */
export const dialog = {
  confirm(opts: Omit<ConfirmSpec, 'kind'>): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      useDialogStore.getState().open({ kind: 'confirm', ...opts, resolve });
    });
  },
  prompt(opts: Omit<PromptSpec, 'kind'>): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      useDialogStore.getState().open({ kind: 'prompt', ...opts, resolve });
    });
  },
};
